// import ThemeToggle from '@/components/common/theme-toggle';
import Calendar from '@/components/calendar/Calendar';
import { Button } from '@/components/common/Button';
import Combobox from '@/components/common/Combobox';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { RadioGroupItem } from '@/components/common/RadioGroup';
import { Textarea } from '@/components/common/Textarea';
import { MAX_LENGTH_DEFAULT_INPUT, MAX_LENGTH_DEFAULT_NOTE, PHONE_VALIDATE_REGEX, ROUTES, SUBMIT_MOMENT_DATE_YMD } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { District, Ward } from '@/types/administrative';
import { LANGUAGES_SUPPORTED, USER_ROLE_TYPE, USER_STATUS_TYPE } from '@/types/enums';
import { formatDateTime } from '@/utils/datetime';
import { toastify } from '@/utils/toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import { RadioGroup } from '@radix-ui/react-radio-group';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

export default observer(function EditUserPage() {
    // hooks
    const { t, i18n: { language } } = useTranslation();
    const { code } = useParams();
    const navigate = useNavigate();

    // store
    const { generalStore: { administrativeUnit }, userStore: { getUser, editUser, user } } = useStore();

    // validate Schema
    const validateSchema = yup.object().shape({
        fullName: yup
            .string()
            .required('form_error_validate.required')
            .max(MAX_LENGTH_DEFAULT_INPUT, 'form_error_validate.full_name_maxlength'),
        birthday: yup
            .string()
            .nullable(),
        phoneNumber: yup
            .string()
            .required('form_error_validate.required')
            .matches(PHONE_VALIDATE_REGEX, 'form_error_validate.format_phone'),
        email: yup
            .string()
            .email('form_error_validate.email')
            .nullable(),
        provinceCode: yup
            .string()
            .required('form_error_validate.required'),
        provinceName: yup
            .string()
            .required('form_error_validate.required'),
        districtCode: yup
            .string()
            .nullable(),
        districtName: yup
            .string(),
        wardCode: yup
            .string()
            .nullable(),
        wardName: yup
            .string(),
        address: yup
            .string()
            .max(MAX_LENGTH_DEFAULT_INPUT, 'form_error_validate.address_maxlength')
            .nullable(),
        status: yup.string().required('form_error_validate.required'),
        role: yup.string().required('form_error_validate.required'),
        note: yup
            .string()
            .max(MAX_LENGTH_DEFAULT_NOTE, 'form_error_validate.note_maxlength')
    });

    type FormData = yup.InferType<typeof validateSchema>;
    const initialValues = {
        fullName: '',
        phoneNumber: '',
        birthday: null,
        email: '',
        provinceCode: '',
        provinceName: '',
        districtCode: '',
        districtName: '',
        wardCode: '',
        wardName: '',
        role: '',
        status: '',
        address: '',
        note: ''
    };

    // state
    const [filteredDistricts, setFilteredDistricts] = useState<District[]>([]);
    const [filteredWards, setFilteredWards] = useState<Ward[]>([]);

    const form = useForm({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: initialValues
    });

    // lifecycle
    useEffect(() => {
        fetchData();
    }, [code]);

    useEffect(() => {
        if (user) {
            setFilteredDistricts(administrativeUnit.districts.filter(district => district.provinceCode === user?.province?.code));
            setFilteredWards(administrativeUnit.wards.filter(ward => ward.districtCode === user?.district?.code));
            form.reset({
                fullName: user?.fullName ?? '',
                phoneNumber: user?.phoneNumber ?? '',
                birthday: user?.birthday ? formatDateTime(user.birthday, SUBMIT_MOMENT_DATE_YMD) : '',
                email: user?.email ?? '',
                provinceCode: user?.province?.code ?? '',
                provinceName: user?.province?.name ?? '',
                districtCode: user?.district?.code ?? '',
                districtName: user?.district?.name ?? '',
                wardCode: user?.ward?.code ?? '',
                wardName: user?.ward?.name ?? '',
                role: user?.role?.toString() ?? '',
                status: user?.status?.toString() ?? '',
                address: user?.address ?? '',
                note: user?.note ?? ''
            });
        }
    }, [user]);

    // function
    const fetchData = async () => {
        if (code) {
            await flowResult(getUser(code));
        }
    };

    const handleDistrictChange = (provinceID: string) => {
        const newDistricts = administrativeUnit.districts.filter(district => district.provinceCode === provinceID);
        setFilteredDistricts(newDistricts);
        form.setValue('districtCode', '');
        form.setValue('districtName', '');
        form.setValue('wardCode', '');
        form.setValue('wardName', '');
        setFilteredWards([]);
    };

    const handleWardChange = (districtID: string) => {
        const newWards = administrativeUnit.wards.filter(ward => ward.districtCode === districtID);
        setFilteredWards(newWards);
        form.setValue('wardCode', '');
        form.setValue('wardName', '');
    };

    const onSubmit = async (data: FormData) => {
        const { fullName, birthday, email, phoneNumber, provinceCode, districtCode, wardCode, address, role, status, note } = data;
        const newBirthday = formatDateTime((birthday || ''), SUBMIT_MOMENT_DATE_YMD);
        const newData = {
            fullName,
            phoneNumber,
            birthday: newBirthday,
            email,
            provinceCode,
            districtCode,
            wardCode,
            address,
            role: Number(role),
            status: Number(status),
            note
        };
        const res = await flowResult(editUser({ code, payload: newData }));
        if (res) {
            navigate(ROUTES.userManagement.href);
            toastify('success', t('messages.update_success'));
        }
    };

    return (
        <>
            <div className='p-4 bg-white'>
                <div className='pt-4 border-t border-input md:border-none md:pt-0'>
                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)}>
                            <div className='lg:flex block gap-20'>
                                <div className='w-full'>
                                    <FormField
                                        control={form.control}
                                        name='fullName'
                                        render={({ field }) => (
                                            <FormItem className=''>
                                                <div className='flex items-center'>
                                                    <Label className='label-1 w-[25%]' required>{t('words_title.full_name')}</Label>
                                                    <div className='w-full'>
                                                        <FormControl className='w-full'>
                                                            <Input
                                                                type='text'
                                                                {...field}
                                                            />
                                                        </FormControl>
                                                    </div>
                                                </div>
                                                <div className='flex items-center'>
                                                    <div className='w-[25%]'></div>
                                                    <FormMessage className='w-full' />
                                                </div>
                                            </FormItem>
                                        )}
                                    />
                                </div>
                                <div className='w-full'>
                                    <FormField
                                        control={form.control}
                                        name='birthday'
                                        render={({ field }) => (
                                            <FormItem>
                                                <div className='flex items-center space-y-0'>
                                                    <Label className='label-1 w-[25%]'>{t('words_title.birthday')}</Label>
                                                    <FormControl>
                                                        <div className='mt-[0.375rem] w-full'>
                                                            <Calendar
                                                                placeholder={t('placeholder.please_select')}
                                                                type='datePicker'
                                                                mode='single'
                                                                selected={field.value ? new Date(field.value) : undefined}
                                                                onSelect={field.onChange}
                                                                required={true}
                                                                disabled={[
                                                                    {
                                                                        after: new Date()
                                                                    }
                                                                ]}
                                                            />
                                                        </div>
                                                    </FormControl>
                                                </div>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>
                            <div className='lg:flex block gap-20 mt-[1rem]'>
                                <div className='w-full'>
                                    <FormField
                                        control={form.control}
                                        name='phoneNumber'
                                        render={({ field }) => (
                                            <FormItem className=''>
                                                <div className='flex items-center'>
                                                    <Label className='label-1 w-[25%]' required>{t('words_title.phone_number')}</Label>
                                                    <div className='w-full'>
                                                        <FormControl>
                                                            <Input
                                                                maxLength={10}
                                                                isTypingOnlyNumber={true}
                                                                type='text'
                                                                {...field}
                                                            />
                                                        </FormControl>
                                                    </div>
                                                </div>
                                                <div className='flex items-center'>
                                                    <div className='w-[25%]'></div>
                                                    <FormMessage className='w-full' />
                                                </div>
                                            </FormItem>
                                        )}
                                    />
                                </div>
                                <div className='w-full'>
                                    <FormField
                                        control={form.control}
                                        name='email'
                                        render={({ field }) => (
                                            <FormItem className='flex items-center space-y-0'>
                                                <Label className='label-1 w-[25%]'>{t('words_title.email')}</Label>
                                                <div className='w-full'>
                                                    <FormControl>
                                                        <Input
                                                            type='text'
                                                            {...field}
                                                            value={field.value ?? ''}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </div>
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>
                            <div className='lg:flex block gap-20 mt-[1rem]'>
                                <div className='w-full'>
                                    <FormField
                                        control={form.control}
                                        name='provinceName'
                                        render={({ field }) => (
                                            <FormItem>
                                                <div className='flex items-center'>
                                                    <Label className='label-1 w-[25%]' required>{t('words_title.city')}</Label>
                                                    <FormControl>
                                                        <Combobox
                                                            value={field.value}
                                                            onChange={(item) => {
                                                                field.onChange(item?.value);
                                                                form.setValue('provinceCode', item?.code);
                                                                handleDistrictChange(item?.code);
                                                            }}
                                                            items={administrativeUnit?.provinces?.map((item, index) => (
                                                                {
                                                                    label: language === LANGUAGES_SUPPORTED.English ? item.fullNameEn : item.fullName,
                                                                    value: item.name,
                                                                    code: item.code
                                                                }
                                                            ))}
                                                            label={t('words_title.city')}
                                                            required
                                                            placeholder={t('placeholder.please_select')}
                                                        />
                                                    </FormControl>
                                                </div>

                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                                <div className='w-full'>
                                    <FormField
                                        control={form.control}
                                        name='districtName'
                                        render={({ field }) => (
                                            <FormItem>
                                                <div className='flex items-center'>
                                                    <Label className='label-1 w-[25%]'>{t('words_title.district')}</Label>
                                                    <FormControl>
                                                        <Combobox
                                                            value={field.value!}
                                                            onChange={(item) => {
                                                                field.onChange(item?.value);
                                                                form.setValue('districtCode', item?.code);
                                                                handleWardChange(item?.code);
                                                            }}
                                                            items={filteredDistricts?.map((item, index) => (
                                                                {
                                                                    label: language === LANGUAGES_SUPPORTED.English ? item.fullNameEn : item.fullName,
                                                                    value: item.name,
                                                                    code: item.code
                                                                }
                                                            ))}
                                                            placeholder={t('placeholder.please_select')}
                                                            disabled={(filteredDistricts && filteredDistricts?.length === 0) ? true : false}
                                                        />
                                                    </FormControl>
                                                </div>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>
                            <div className='lg:flex block gap-20 mt-[1rem]'>
                                <div className='w-full'>
                                    <FormField
                                        control={form.control}
                                        name='wardName'
                                        render={({ field }) => (
                                            <FormItem>
                                                <div className='flex items-center'>
                                                    <Label className='label-1 w-[25%]'>{t('words_title.ward')}</Label>

                                                    <FormControl>
                                                        <Combobox
                                                            value={field.value!}
                                                            onChange={(item) => {
                                                                field.onChange(item?.value);
                                                                form.setValue('wardCode', item?.code);
                                                            }}
                                                            items={filteredWards?.map((item, index) => (
                                                                {
                                                                    label: language === LANGUAGES_SUPPORTED.English ? item.fullNameEn : item.fullName,
                                                                    value: item.name,
                                                                    code: item.code
                                                                }
                                                            ))}
                                                            placeholder={t('placeholder.please_select')}
                                                            disabled={(filteredWards && filteredWards?.length === 0) ? true : false}
                                                        />
                                                    </FormControl>
                                                </div>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                                <div className='w-full'>
                                    <FormField
                                        control={form.control}
                                        name='address'
                                        render={({ field }) => (
                                            <FormItem>
                                                <div className='flex items-center'>
                                                    <Label className='label-1 w-[25%]'>{t('words_title.address')}</Label>
                                                    <FormControl>
                                                        <Input
                                                            type='text'
                                                            {...field}
                                                            value={field.value ?? ''}
                                                        />
                                                    </FormControl>
                                                </div>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>
                            </div>
                            <div className='lg:flex block gap-20 mt-[1rem]'>
                                <div className='w-full'>
                                    <FormField
                                        control={form.control}
                                        name='role'
                                        render={({ field }) => (
                                            <>
                                                <FormItem>
                                                    <div className='flex items-center my-5'>
                                                        <Label className='label-1 w-[25%]' required>{t('words_title.role')}</Label>
                                                        <FormControl>
                                                            <div className='w-full my-2'>
                                                                <RadioGroup
                                                                    className='flex gap-10 ms-2'
                                                                    value={field.value}
                                                                    onValueChange={field.onChange}
                                                                    defaultValue={USER_ROLE_TYPE.Admin.toString()}
                                                                >
                                                                    <div className='flex items-center space-x-2'>
                                                                        <RadioGroupItem value={USER_ROLE_TYPE.Admin.toString()} id='admin' />
                                                                        <Label htmlFor='admin'>{t('words_title.administrator')}</Label>
                                                                    </div>
                                                                    <div className='flex items-center space-x-2'>
                                                                        <RadioGroupItem value={USER_ROLE_TYPE.Enduser.toString()} id='user' />
                                                                        <Label htmlFor='user'>{t('words_title.user')}</Label>
                                                                    </div>

                                                                </RadioGroup>
                                                            </div>
                                                        </FormControl>
                                                    </div>
                                                    <FormMessage />
                                                </FormItem>
                                            </>
                                        )}
                                    />
                                </div>
                                <div className='w-full'>
                                    <FormField
                                        control={form.control}
                                        name='status'
                                        render={({ field }) => (
                                            <>
                                                <FormItem>
                                                    <div className='flex items-center my-5'>
                                                        <Label className='label-1 w-[25%]' required>{t('words_title.status')}</Label>
                                                        <FormControl>
                                                            <div className='w-full my-2'>
                                                                <RadioGroup
                                                                    className='flex gap-10 ms-2'
                                                                    value={field.value}
                                                                    onValueChange={field.onChange}
                                                                    defaultValue={USER_ROLE_TYPE.Admin.toString()}
                                                                >
                                                                    <div className='flex items-center space-x-2'>
                                                                        <RadioGroupItem value={USER_STATUS_TYPE.Active.toString()} id='active' />
                                                                        <Label htmlFor='active'>{t('words_title.active_action')}</Label>
                                                                    </div>
                                                                    <div className='flex items-center space-x-2'>
                                                                        <RadioGroupItem value={USER_STATUS_TYPE.Inactive.toString()} id='inactive' />
                                                                        <Label htmlFor='inactive'>{t('words_title.inactive_action')}</Label>
                                                                    </div>

                                                                </RadioGroup>
                                                            </div>
                                                        </FormControl>
                                                    </div>
                                                    <FormMessage />
                                                </FormItem>
                                            </>
                                        )}
                                    />
                                </div>
                            </div>
                            <FormField
                                control={form.control}
                                name='note'
                                render={({ field }) => (
                                    <div>
                                        <FormItem className='flex space-y-0 mt-2'>
                                            <Label className='label-1 w-[11%]'>{t('words_title.note')}</Label>
                                            <div className='mt-4 w-full'>
                                                <FormControl>
                                                    <Textarea
                                                        className='whitespace-pre-line'
                                                        maxLength={2000}
                                                        {...field}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </div>
                                        </FormItem>
                                    </div>
                                )}
                            />
                            <div className='mt-[1rem] w-1/2 mx-auto gap-10 md:mt-10 flex mb-5'>
                                <Button
                                    type='button'
                                    onClick={() => {
                                        navigate(ROUTES.userManagement.href);
                                    }}
                                    className='w-1/2'
                                    variant='outline'
                                >
                                    {t('button.cancel')}
                                </Button>
                                <Button type='submit' disabled={form.formState.isSubmitting} className='w-1/2'>{t('button.save')}</Button>
                            </div>
                        </form>
                    </Form>
                </div>
            </div>
        </>
    );
});
